import React, {useEffect, useState} from 'react';
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';
import EditIcon from '@material-ui/icons/Edit';
import {
  changeName,
  selectName
} from "./redux/project-slice";
import {useSelector, useDispatch} from 'react-redux';
import {Projection, changeData, changeView, selectData} from './redux/projections-slice';
import projections, {
  generateID,
  unknownProjectionFeet,
  unknownProjectionMeters,
  unknownTextFeet,
  unknownTextMeters
} from './projections/projections';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  TextField
} from '@material-ui/core';
import {assetDrawerWidth} from './asset-drawer';
import LocalScene, { infoToMeters, projectionUnits } from './viewer/js/projections';
import {dialog, isStaticSite} from "./electron-modules";
import { useSnackbar } from 'notistack';
import slash from 'slash';
import {
  changeBasicAdjustments,
  changeCameraHeight,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraHeight,
  selectCameraTransform
} from './redux/camera-slice';
import { readTransformFile, cameraMatrixFilter, saveMatrixFile } from './camera-utils';
import clsx from 'clsx';
import { ArrowTooltip } from './tooltips';
import { Viewer } from './viewer/js/main';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warning: {
      color: theme.palette.warning.main
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    title: {
      flexGrow: 1,
    },
    projectName: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
      flexGrow: 1
    },
    projectionText: {
      paddingTop: theme.spacing(0),
      marginTop: "-5px",
      color: "white"
    },
    transformMatrix: {
      maxWidth: "60px",
      paddingRight: "10px"
    },
    matrixColumn: {
      display: "flex",
      flexDirection: "column"
    },
    matrix: {
      display: "flex"
    },
    marginTop: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1)
    },
    buttonDiv: {
      textAlign: "right"
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    slimButton: {
      marginTop: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    offset: {
      marginLeft: assetDrawerWidth - 60
    },
    textField: {
      paddingBottom: theme.spacing(3)
    },
    matrixAndButtons: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    verticalButtons: {
      display: "flex",
      flexDirection: "column"
    }
  }),
);

const openDialogMatrix = (): Promise<string> => {
  return dialog.showOpenDialog( {
    filters: cameraMatrixFilter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return [];
  });
};

const MatrixTextField = (props) => {
  const {value, classes} = props;

  /** Truncate and set fixed number of digits */
  const truncate = (number, digits=3) => {
    const multiplier = Math.pow(10, digits);
    return (Math.trunc(number * multiplier) / multiplier)
      .toFixed(digits);
  };

  return (
    <ArrowTooltip title={value}>
      <TextField
        disabled
        inputProps={{min: 0, style: { textAlign: 'center' }}}
        className={classes.transformMatrix}
        value={truncate(value)}
      />
    </ArrowTooltip>
  );
};

const MatrixRepresentation = (props) => {
  const {classes, cameraTransform} = props;
  const matrix = cameraTransform.matrix;

  return (
    <div className={classes.matrix}>
      <div className={classes.matrixColumn}>
        <MatrixTextField classes={classes} value={matrix[0]}/>
        <MatrixTextField classes={classes} value={matrix[4]}/>
        <MatrixTextField classes={classes} value={matrix[8]}/>
        <MatrixTextField classes={classes} value={matrix[12]}/>
      </div>

      <div className={classes.matrixColumn}>
        <MatrixTextField classes={classes} value={matrix[1]}/>
        <MatrixTextField classes={classes} value={matrix[5]}/>
        <MatrixTextField classes={classes} value={matrix[9]}/>
        <MatrixTextField classes={classes} value={matrix[13]}/>
      </div>

      <div className={classes.matrixColumn}>
        <MatrixTextField classes={classes} value={matrix[2]}/>
        <MatrixTextField classes={classes} value={matrix[6]}/>
        <MatrixTextField classes={classes} value={matrix[10]}/>
        <MatrixTextField classes={classes} value={matrix[14]}/>
      </div>

      <div className={classes.matrixColumn}>
        <MatrixTextField classes={classes} value={matrix[3]}/>
        <MatrixTextField classes={classes} value={matrix[7]}/>
        <MatrixTextField classes={classes} value={matrix[11]}/>
        <MatrixTextField classes={classes} value={matrix[15]}/>
      </div>
    </div>
  );
};

const ProjectDetails = (props) => {  const dispatch = useDispatch();
  const classes = useStyles();
  const showMessage = useSnackbar().enqueueSnackbar;

  const viewer = props.viewer as Viewer;
  const {assetDrawerState} = props;

  const [open, setOpen] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [name, setName] = useState("");
  const [height, setHeight] = useState(0);
  const [proj, setProj] = useState("");
  const [imported, setImported] = useState<Projection>(null);
  const [cameraTransform, setCameraTransform] = useState({
    matrix: null,
    path: null
  });
  const [errors, setErrors] = useState({
    name: false,
    height: false,
    projection: false,
    correction: false
  });

  const projectName = useSelector(selectName);
  const projectHeight = useSelector(selectCameraHeight);
  const cameraTransformPath = useSelector(selectCameraTransform);
  const dataProjection = useSelector(selectData);
  const basicAdjustments = useSelector(selectBasicAdjustments);

  const getNewProjection = () => {
    let newProjection = null;
    if (proj === unknownTextMeters) {
      newProjection = unknownProjectionMeters;
    } else if (proj === unknownTextFeet) {
      newProjection = unknownProjectionFeet;
    } else {
      const results = data.filter(x => x.name === proj);
      if (results.length !== 1) {
        return;
      }
      newProjection = results[0];
    }
    return newProjection;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleWarnClose = () => {
    setWarnOpen(false);
  };

  const updateName = (event) => {
    const value = event.target.value;
    setErrors({...errors, name: false});
    setName(value);
  };

  const updateHeight = (event) => {
    const value = event.target.value;
    setErrors({...errors, height: false});
    setHeight(value);
  };

  const updateProj = (event) => {
    const value = event.target.value;
    setErrors({...errors, projection: false});
    setProj(value);
  };

  const onSubmit = () => {
    const trimmedName = name.trim();
    const emptyName = trimmedName === "";
    const emptyHeight = (height as any) === "";
    const emptyProjection = proj === "";

    setErrors({
      name: emptyName,
      height: emptyHeight,
      projection: emptyProjection,
      correction: false
    });

    if (emptyName || emptyProjection || emptyHeight) {
      return;
    }

    const heightInMeters = height * toMeters;

    setOpen(false);
    showMessage("Project details updated", {
      variant: "success"
    });

    setTimeout(() => {
      dispatch(changeName(trimmedName));
      dispatch(changeCameraHeight(heightInMeters));
      dispatch(changeCameraTransform(cameraTransform.path));

      // Projection change requires a more strict check before dispatch
      const projName = selectedProjection.name;
      const projChanged = !LocalScene.currentProjection(projName);

      if (projChanged) {
        dispatch(changeData(selectedProjection));
        dispatch(changeView(selectedProjection));
      }
    }, 50);
  };

  const handleCollisions = () => {
    const name = dataProjection.name;
    if ((name === unknownTextFeet) || (name === unknownTextMeters)) {
      return;
    }

    if (imported?.name === name) {
      return;
    }

    const results = projections.data.filter(x => x.name === name);

    if (results.length > 0) {
      // Compare projection with existing projections
      const existing = results[0];
      const importedProjection = JSON.stringify(dataProjection, Object.keys(dataProjection).sort());
      const userProjection = JSON.stringify(existing, Object.keys(existing).sort());

      if (importedProjection === userProjection) {
        // Projections are the same, do nothing
        return;
      } else {
        // Projections are different, so we warn the user
        setWarnOpen(true);
      }
    }

    setImported({...dataProjection, imported: true});
  };

  const onCorrectionSelect = () => {
    openDialogMatrix().then(filePath => {
      if (!filePath) {
        return;
      }

      readTransformFile(filePath)
        .then(data => {
          const {success, content} = data;

          if (success) {
            setCameraTransform({
              path: filePath,
              matrix: content
            });
          } else {
            showMessage(content, {
              variant: "error"
            });
          }
        });
    });
  };

  const onCorrectionRemove = () => {
    setCameraTransform({
      path: null,
      matrix: null
    });
  };

  const updateTitle = (title) => {
    document.title = "SOLV3D engine viewer - " + title;
  };

  const clearBasicAlignment = () => {
    dispatch(changeBasicAdjustments({}));
    viewer?.resetBasicAlignerCamera();
  };

  useEffect(() => {
    setProj(dataProjection.name);
  }, [dataProjection]);

  useEffect(() => {
    setName(projectName);
    updateTitle(projectName);
  }, [projectName]);

  useEffect(() => {
    readTransformFile(cameraTransformPath)
      .then(data => {
        const {success, content} = data;

        if (success) {
          setCameraTransform({
            path: cameraTransformPath,
            matrix: content
          });
        } else {
          setCameraTransform({
            path: null,
            matrix: null
          });
        }
      });
  }, [cameraTransformPath]);

  useEffect(() => {
    const height = projectHeight / toMeters;
    setHeight(height);
  }, [open]);

  handleCollisions();

  const data = projections.data;
  data.sort((a, b) => {
    const name1 = a.name.toLowerCase();
    const name2 = b.name.toLowerCase();
    return (name1 < name2) ? -1 : ((name2 > name1) ? 1 : 0);
  });

  const matchingProjection = projections.data?.filter(
    x => x.name === dataProjection.name)[0];

  const defaultProjection = (dataProjection.name === unknownTextMeters)
    || (dataProjection.name === unknownTextFeet);

  const selectedProjection = getNewProjection();
  const units = projectionUnits(selectedProjection);
  const toMeters = infoToMeters(selectedProjection);
  const hasBasicAlignment = Object.keys(basicAdjustments).length !== 0;

  return (
    <div className={classes.projectName}>
      <FormControl
        component="fieldset"
        className={assetDrawerState ? classes.offset : ""}
      >
        <Typography variant="h6" noWrap>
          {projectName}
        </Typography>
        <FormHelperText
          error={defaultProjection}
          className={classes.projectionText}
        >
          {dataProjection.name}
        </FormHelperText>
      </FormControl>

      {(!isStaticSite) && (<IconButton
        onClick={() => {
          setOpen(true);
        }}
        color={"inherit"}
      >
        <EditIcon fontSize={"small"}/>
      </IconButton>)}

      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        onClose={handleClose}
      >
        <DialogTitle>Project Information</DialogTitle>
        <DialogContent>
          {/* Project display title */}
          <InputLabel>Title</InputLabel>
          <Typography variant="caption">
            The display name of the project that is shown when users export/share the project data.
          </Typography>
          <TextField
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            value={name}
            error={errors.name}
            onChange={updateName}
            className={classes.textField}
          />

          {/* Camera height */}
          <InputLabel>Camera Height ({units})</InputLabel>
          <Typography variant="caption">
            The height of the camera from the ground. Used when displaying markers and for image-only measurements.
          </Typography>
          <TextField
            InputLabelProps={{
              shrink: true,
            }}
            type="number"
            fullWidth
            value={height}
            error={errors.height}
            onChange={updateHeight}
            className={classes.textField}
          />

          {/* Projection dropdown */}
          <InputLabel>Data Projection</InputLabel>
          <Typography variant="caption">
            The projection that describes your input data (point cloud and images). Note that
            changing the projection will force a reload of data, and remove all active measurements.
          </Typography>
          <TextField
            select
            fullWidth
            id="viewer-proj-select"
            value={proj}
            error={errors.projection}
            onChange={updateProj}
            className={classes.textField}
          >
            {imported && (<ListSubheader>Imported Projections</ListSubheader>)}

            {imported && (<MenuItem
              key={generateID(imported.name)}
              value={imported.name}
            >{imported.name}</MenuItem>)}

            <ListSubheader>Generic Projections</ListSubheader>

            <MenuItem
              key={generateID(unknownTextMeters)}
              value={unknownTextMeters}
            >{unknownTextMeters}</MenuItem>

            <MenuItem
              key={generateID(unknownTextFeet)}
              value={unknownTextFeet}
            >{unknownTextFeet}</MenuItem>

            <ListSubheader>User Projections</ListSubheader>

            {data.map(row => {
              const conflict = imported ? imported.name === row.name : false;
              const suffix = conflict ? "__conflict" : "";
              return (<MenuItem
                key={generateID(row.name) + suffix}
                value={row.name + suffix}
                disabled={conflict}
                title={conflict ? "Conflict with imported projection" : ""}
              >{row.name}</MenuItem>);
            })}
          </TextField>

          {/* Camera Correction Matrix */}
          <div>
            <InputLabel>Camera Correction File (Optional)</InputLabel>

            {(!hasBasicAlignment) && (<Typography variant="caption">
              An optional camera correction matrix for misaligned cameras. This is only required if
              post-capture data alignment was done using the Data Alignment module.
            </Typography>)}

            {(hasBasicAlignment) && (<div className={classes.marginTop}>
              <Typography variant="caption" className={classes.warning}>
                Image and Point Cloud data currently have a basic alignment applied. Current Alignment
                must be cleared before applying correction matrix
              </Typography>
            </div>)}

            <div className={classes.matrixAndButtons}>
              {cameraTransform.path && (<MatrixRepresentation
                classes={classes}
                cameraTransform={cameraTransform}
              />)}

              <div className={classes.verticalButtons}>
                {/* Save matrix file */}
                {(cameraTransform.path) && (<Button
                  type="submit"
                  onClick={()=>saveMatrixFile(cameraTransform.path, showMessage)}
                  className={clsx(classes.button, classes.slimButton)}
                  color="primary"
                >
                    Save File
                </Button>)}

                {/* Remove matrix file */}
                {cameraTransform.path && (<Button
                  type="submit"
                  onClick={onCorrectionRemove}
                  className={clsx(classes.button, classes.slimButton)}
                  color="secondary"
                >
                    Remove File
                </Button>)}

                {/* Upload matrix file */}
                {(!hasBasicAlignment) && (<Button
                  type="submit"
                  onClick={onCorrectionSelect}
                  className={clsx(classes.button, classes.slimButton)}
                  color="primary"
                >
                  Upload File
                </Button>)}

                {/* Cancel basic alignment */}
                {(hasBasicAlignment) && (<Button
                  onClick={clearBasicAlignment} color="secondary">
                    Clear Alignment
                </Button>)}
              </div>
            </div>

          </div>

          <div className={classes.buttonDiv}>
            <Button
              color="primary"
              type="submit"
              onClick={() => {
                setOpen(false);
              }}
              className={classes.button}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              type="submit"
              onClick={onSubmit}
              className={classes.button}
            >
              Update Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Warning message for projection mismatch */}
      <Dialog
        open={warnOpen}
        onClose={handleWarnClose}
        aria-labelledby="warn-dialog-title"
        aria-describedby="warn-dialog-description"
      >
        <DialogTitle id="warn-dialog-title">{"Projection Mismatch"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="warn-dialog-description">
            The projection <b>({dataProjection.name})</b> imported from the project file does not match the
            user projection of the same name. To ensure compatability, the user projection will be disabled
            in the project edit screen.
          </DialogContentText>

          <DialogContentText>
            <b>Imported Projection{dataProjection.transform ? " (+ Custom Transform)" : ""}</b>
            <br></br>
            "{dataProjection.string}"
          </DialogContentText>

          <DialogContentText>
            <b>Existing Projection{matchingProjection?.transform ? " (+ Custom Transform)" : ""}</b>
            <br></br>
            "{matchingProjection?.string}"
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarnClose} color="primary">
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const MainAppBar = (props) => {
  const classes = useStyles();

  const viewer = props.viewer as Viewer;
  const {handleAssetDrawerOpen, handleSettingsDrawerOpen, assetDrawerState} = props;

  return (
    <AppBar position="static">
      <Toolbar>
        <IconButton
          edge="start"
          className={classes.menuButton}
          color="inherit"
          aria-label="assets menu"
          onClick={handleAssetDrawerOpen}
        >
          <MenuIcon/>
        </IconButton>
        <ProjectDetails
          viewer={viewer}
          assetDrawerState={assetDrawerState}
        />
        <IconButton
          color="inherit"
          aria-label="settings menu"
          onClick={handleSettingsDrawerOpen}
        >
          <SettingsIcon/>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};